import React, { useState } from 'react';
import { Upload, Modal, Select } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { FlexColumn, Flex } from '../../Reusable/Container';
import { Text } from '../../Reusable/Text';
import { Button } from '../../Reusable/Button';
import { LabeledInput } from '../../Reusable/Input';
import { ErrorAlert } from '../../Reusable/Alert';
import ModalClose from '../../../Images/modal-close.png';
import environment from '../../../environment';

const { Dragger } = Upload;
const { Option } = Select;

const docTypes = [
    {
        name: "W-9",
        type: "W-9",
    },
    {
        name: "Purchase Order",
        type: "PO",
    },
    {
        name: "Work Order",
        type: "WO",
    },
    {
        name: "Master Service Agreement",
        type: "MSA",
    },
    {
        name: "Other",
        type: "OTHER",
    }
];

const DocumentUploadModal = ({ 
    visible, 
    onClose, 
    onSave, 
    doc = {}, 
    businessKey, 
    jwt 
}) => {
    const [uploadedFile, setUploadedFile] = useState({ ...doc });
    const [originalFileName, setOriginalFileName] = useState('');
    const [selectedType, setSelectedType] = useState(doc.type || '');

    const uploadProps = {
        name: 'payables-docs',
        multiple: false,
        action: `${environment.apiBaseUrl}/file/upload`,
        headers: {
            businesskey: businessKey,
            jwt: jwt
        },
        onChange: (info) => {
            const { file } = info;
            const { status, response, name } = file;
            
            if (status === "done") {
                // Store the original file name for "OTHER" type
                setOriginalFileName(name);
                
                // Set the file properties
                setUploadedFile({
                    ...uploadedFile,
                    uri: (response.UploadedUrls || [])[0],
                    fileName: name,  // Always use original file name
                    type: selectedType
                });
            } else if (status === "error") {
                ErrorAlert({ description: "File upload failed. Please try again." });
            }
        }
    };

    const handleSave = () => {
        if (!uploadedFile.uri) {
            ErrorAlert({ description: "Please upload a file before saving" });
            return;
        }

        if (doc.type !== "W-9" && !uploadedFile.type) {
            ErrorAlert({ description: "Please select a document type" });
            return;
        }

        // Prepare the final attachment object
        const finalAttachment = {
            ...uploadedFile,
            // Set the filename based on type
            fileName: getFileName()
        };

        onSave(finalAttachment);
    };

    // Always use the original file name uploaded by the user
    const getFileName = () => {
        return originalFileName;
    };

    return (
        <Modal
            visible={visible}
            footer={null}
            closable={true}
            width={550}
            destroyOnClose={true}
            onCancel={onClose}
            closeIcon={<img width='24px' height='24px' src={ModalClose} />}
        >
            <FlexColumn between gap='24px' fullWidth>
                <FlexColumn left start fullWidth>
                    <Text heading>Upload {doc.name === "W-9" ? "W-9" : "document"}</Text>
                    <Text>Upload your document and add a note.</Text>
                    
                    <FlexColumn start gap='24px' style={{ marginTop: 24 }} fullWidth>
                        <Dragger {...uploadProps}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text" style={{ padding: '0 24px' }}>
                                Click or drag file to this area to upload
                            </p>
                            {uploadedFile.uri && (
                                <p className="ant-upload-hint" style={{ color: 'green' }}>
                                    File uploaded successfully: {originalFileName}
                                </p>
                            )}
                        </Dragger>

                        <LabeledInput
                            label="Document type"
                            type="select"
                            size="type"
                            key="type"
                            value={selectedType}
                            placeholder="Select document type"
                            optionFilterProp="children"
                            onChange={(value) => {
                                setSelectedType(value);
                                setUploadedFile({ ...uploadedFile, type: value });
                            }}
                            className="no-left-padding"
                        >
                            {docTypes.map(item => (
                                <Option 
                                    key={item.type} 
                                    id="type" 
                                    name={item.name} 
                                    value={item.type} 
                                    style={{ backgroundColor: "transparent" }}
                                >
                                    {item.name}
                                </Option>
                            ))}
                        </LabeledInput>

                        <LabeledInput
                            label="Note"
                            placeholder="Note"
                            nomargin
                            key="note"
                            id="note"
                            onChange={e => setUploadedFile({ ...uploadedFile, notes: e.target.value })}
                            optional
                        />

                        <Flex start>
                            <Button 
                                solid 
                                permtype="Override" 
                                onClick={handleSave}
                                text='Save' 
                                disabled={!uploadedFile.uri || !selectedType}
                            />
                        </Flex>
                    </FlexColumn>
                </FlexColumn>
            </FlexColumn>
        </Modal>
    );
};

export default DocumentUploadModal;